// MACHINERY V2 (Stroje) — the katalog PAGE HEAD below 1280 (M2.16c).
//
// Rendered truth: mobile/screenshots/16-katalog-list.png. Reference impl:
// mobile/machinery-mobile.jsx MbPageHead (:30-41), the same head
// fleet/FleetMobileHeader.tsx builds for the fleet.
//
// Below 1280 the rail and the desktop breadcrumb band are gone, so the head is
// where "which page am I on" and "how big is this catalog" live.
//
// ============================================================
//  >>> DELIBERATE DEVIATION 1 OF 2 HERE: THE META LINE DROPS "57 OEM ready".
// ============================================================
// The board's meta reads "124 modelov · 57 OEM ready". The first number is real
// — `browseTotalCount` is the server's own `result.totalCount`
// (useCatalogBrowse.ts). THE SECOND ONE DOES NOT EXIST: there is no
// readiness-tier breakdown anywhere in the catalog API. `useCatalogBrowse`
// receives `totalCount` plus per-CATEGORY facet counts and nothing else, and
// counting the loaded PAGE would print at most `pageSize` under a label that
// claims to describe the whole catalog. This is the same missing datum that
// leaves ReadinessLegendSheet.tsx without its per-tier counts; ONE backend ask
// covers both. Do not "finish" this line from the page — a page number under a
// catalog label is the data-honesty failure this project has ruled against
// before.
//
// ============================================================
//  THE READINESS LEGEND OPENS FROM HERE, AND IT IS NOT A ROUTE
// ============================================================
// Board 19 draws the three-tier explainer as a full page; it ships as a sheet
// off this button instead. A guarded route for a help surface costs a route, a
// guard, a back-stack entry and a nav decision, and buys nothing a sheet does
// not already give — and the sheet keeps the user's list, filters and scroll
// position underneath it.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";

/** Circled "i" — the conventional help affordance, drawn at the same 1.4 stroke
 *  weight as the kit's other 44px-target glyphs (mobile/mobileGlyphs.tsx). */
const InfoGlyph: React.FC = () => (
  <svg
    viewBox="0 0 16 16"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.4"
    strokeLinecap="round"
    aria-hidden
  >
    <circle cx="8" cy="8" r="6.4" />
    <path d="M8 7.2 L8 11.2" />
    <path d="M8 4.9 L8 5.1" />
  </svg>
);

export interface CatalogMobileHeaderProps {
  /** The server's own total for the current category + filters. */
  totalCount: number;
  onOpenLegend: () => void;
}

const CatalogMobileHeader: React.FC<CatalogMobileHeaderProps> = ({
  totalCount,
  onOpenLegend,
}) => {
  const { translate } = useTranslation("myMachinery");

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "12px 14px 10px",
        flexShrink: 0,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontFamily: "var(--font-sans)",
            fontSize: 18,
            fontWeight: 700,
            letterSpacing: "-0.01em",
            color: "var(--ink)",
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {translate("myMachinery:machineCatalog")}
        </div>
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            color: "var(--faint)",
            letterSpacing: "0.04em",
            marginTop: 1,
          }}
        >
          {translate("myMachinery:v2.catalog.modelCount", { count: totalCount })}
        </div>
      </div>

      <span style={{ flex: 1 }} />

      <button
        type="button"
        className="btn"
        onClick={onOpenLegend}
        aria-haspopup="dialog"
        // NAMES WHAT IT OPENS. The glyph is decorative, so the accessible name is
        // the only thing a screen reader has to go on.
        aria-label={translate("myMachinery:midasReadiness")}
        title={translate("myMachinery:midasReadiness")}
        style={{
          // >= 44px BOTH axes — an icon-only control has no label to widen it.
          // `minWidth` accompanies `width` because this is a flex child: a
          // sibling that grows can shrink a `width` but never a `minWidth`.
          width: 44,
          minWidth: 44,
          height: 44,
          padding: 0,
          borderRadius: 10,
          justifyContent: "center",
          flexShrink: 0,
          color: "var(--mod-accent)",
        }}
      >
        <InfoGlyph />
      </button>
    </div>
  );
};

export default CatalogMobileHeader;
